document.documentElement.classList.remove('no-js');

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('[data-ridge-header]');
  const mega = header?.querySelector('[data-mega-menu]');
  const megaTriggers = header?.querySelectorAll('[data-mega-trigger]') || [];
  const megaPanels = header?.querySelectorAll('[data-mega-panel]') || [];
  const mobileToggle = header?.querySelector('[data-mobile-menu-toggle]');
  const mobileMenu = header?.querySelector('[data-mobile-menu]');

  let megaCloseTimer = null;
  let megaExitTimer = null;
  let activeMegaIndex = null;

  const cancelMegaClose = () => {
    if (megaCloseTimer) { clearTimeout(megaCloseTimer); megaCloseTimer = null; }
  };

  const cancelMegaExit = () => {
    if (megaExitTimer) { clearTimeout(megaExitTimer); megaExitTimer = null; }
  };

  const scheduleMegaExit = () => {
    if (!desktopHoverMega.matches || !header?.classList.contains('is-mega-open')) return;
    cancelMegaExit();
    megaExitTimer = setTimeout(() => {
      megaExitTimer = null;
      closeMega(false);
    }, 90);
  };

  const closeMega = (immediate = false) => {
    if (!header) return;
    // Do absolutely nothing unless a mega menu is actually open/closing.
    // This prevents pointerleave from briefly creating the cream closing state
    // when the user never opened a menu in the first place.
    const hasMegaState = header.classList.contains('is-mega-open') || header.classList.contains('is-mega-closing') || activeMegaIndex !== null;
    if (!hasMegaState) return;
    cancelMegaClose();
    cancelMegaExit();
    const finish = () => {
      header.classList.remove('is-mega-open','is-mega-closing');
      mega?.setAttribute('aria-hidden', 'true');
      megaTriggers.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
      megaPanels.forEach(panel => panel.classList.remove('is-active'));
      activeMegaIndex = null;
    };
    if (immediate) { finish(); return; }
    // Keep the same surface while the drawer collapses so no outline/two-tone flash appears.
    header.classList.add('is-mega-closing');
    header.classList.remove('is-mega-open');
    megaTriggers.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
    megaCloseTimer = setTimeout(finish, 390);
  };

  const openMega = trigger => {
    if (!header || !trigger) return;
    cancelMegaClose();
    cancelMegaExit();
    const index = trigger.dataset.megaTrigger;
    const samePanel = activeMegaIndex === index && header.classList.contains('is-mega-open');
    if (samePanel) return;
    activeMegaIndex = index;
    header.classList.remove('is-mega-closing');
    header.classList.add('is-mega-open');
    mega?.setAttribute('aria-hidden', 'false');
    megaTriggers.forEach(btn => btn.setAttribute('aria-expanded', String(btn === trigger)));
    megaPanels.forEach(panel => panel.classList.toggle('is-active', panel.dataset.megaPanel === index));

    // Ridge 3.0.86 — size the desktop drawer to its real active content.
    // This keeps the bottom edge just below the final link instead of
    // reserving a large fixed-height panel.
    requestAnimationFrame(() => {
      if (!mega) return;
      const inner = mega.querySelector('.ridge-mega__inner');
      if (!inner) return;
      const frame = header.querySelector('.ridge-header__frame');
      const frameBottom = frame ? Math.ceil(frame.getBoundingClientRect().bottom) : 86;
      const contentHeight = Math.ceil(inner.scrollHeight);
      mega.style.setProperty('--ridge-mega-open-height', `${contentHeight}px`);
      header.style.setProperty('--ridge-mega-content-top', `${frameBottom}px`);
      header.style.setProperty('--ridge-mega-sheet-height', `${frameBottom + contentHeight}px`);
    });
  };

  // Ridge 3.0.85 — Oura-style desktop activation.
  // Fine-pointer desktop devices open the mega menu directly on hover/focus.
  // Click remains available for coarse pointers/touch and keyboard fallback.
  const desktopHoverMega = window.matchMedia('(hover: hover) and (pointer: fine)');

  megaTriggers.forEach(trigger => {
    trigger.addEventListener('pointerenter', () => {
      if (desktopHoverMega.matches) openMega(trigger);
    });

    trigger.addEventListener('focus', () => {
      if (desktopHoverMega.matches) openMega(trigger);
    });

    trigger.addEventListener('click', e => {
      e.preventDefault();
      if (desktopHoverMega.matches) {
        // Mouse users already opened it on hover; keep click from collapsing it.
        openMega(trigger);
        return;
      }
      const isSame = activeMegaIndex === trigger.dataset.megaTrigger && header?.classList.contains('is-mega-open');
      if (isSame) closeMega(false);
      else openMega(trigger);
    });
  });

  mega?.addEventListener('pointerenter', () => {
    cancelMegaClose();
    cancelMegaExit();
  });
  header?.addEventListener('pointerenter', () => {
    cancelMegaClose();
    cancelMegaExit();
  });

  // Ridge 3.0.90 — the desktop mega remains open while the pointer is
  // anywhere inside the cream expanded sheet, not merely inside the DOM
  // bounds of the original floating header. This creates a continuous hover
  // zone from the top of the viewport to the bottom of the expanded menu.
  document.addEventListener('pointermove', e => {
    if (!desktopHoverMega.matches || !header?.classList.contains('is-mega-open')) return;

    const sheetHeightRaw = getComputedStyle(header).getPropertyValue('--ridge-mega-sheet-height');
    const sheetBottom = parseFloat(sheetHeightRaw) || 0;
    const insideExpandedSheet =
      e.clientX >= 0 &&
      e.clientX <= window.innerWidth &&
      e.clientY >= 0 &&
      e.clientY <= sheetBottom + 2;

    if (insideExpandedSheet) {
      cancelMegaExit();
    } else {
      scheduleMegaExit();
    }
  }, { passive: true });

  window.addEventListener('blur', () => {
    cancelMegaExit();
    if (header?.classList.contains('is-mega-open')) closeMega(false);
  });

  // Ridge 3.0.54 — CSS-driven mobile drawer.
  // The entire sheet now opens/closes from a single class transition, matching
  // the desktop mega-menu feel and avoiding inline-height animation flashes.
  const setMobileOpen = open => {
    if (!header || !mobileToggle || !mobileMenu) return;
    if (open) {
      window.clearTimeout(header._ridgeMobileCloseTimer);
      header.classList.remove('is-mobile-closing');
      closeMega(true);

      // Ridge 3.0.86 — measure the existing mobile menu content so its
      // final size stays exactly the same, while max-height animates the
      // panel smoothly downward from the nav.
      const inner = mobileMenu.querySelector('.ridge-mobile-menu__inner');
      if (inner) {
        mobileMenu.style.setProperty('--ridge-mobile-menu-open-height', `${Math.ceil(inner.scrollHeight)}px`);
      }

      header.classList.add('is-mobile-open');
      mobileToggle.setAttribute('aria-expanded', 'true');
      mobileMenu.setAttribute('aria-hidden', 'false');
    } else {
      // Ridge 3.1.00 — first force the text into a hidden closing state,
      // then start the cream-sheet collapse on the next animation frame.
      // This prevents even a single lingering frame of submenu copy.
      header.classList.add('is-mobile-closing');
      mobileToggle.setAttribute('aria-expanded', 'false');
      mobileMenu.setAttribute('aria-hidden', 'true');

      requestAnimationFrame(() => {
        header.classList.remove('is-mobile-open');
      });

      window.clearTimeout(header._ridgeMobileCloseTimer);
      header._ridgeMobileCloseTimer = window.setTimeout(() => {
        header.classList.remove('is-mobile-closing');
      }, 760);
    }
  };

  mobileToggle?.addEventListener('click', () => {
    setMobileOpen(!header.classList.contains('is-mobile-open'));
  });

  // Ridge 3.1.08 — CSS-grid mobile accordions.
  // No measured heights, timers, transitionend cleanup, or post-animation
  // corrections. The browser animates the submenu's real layout height.
  const mobileMenuInner = mobileMenu?.querySelector('.ridge-mobile-menu__inner');

  // Keep the outer cream drawer matched to the real inner height as CSS-grid
  // accordions expand/retract. This preserves the smooth accordion behavior
  // without returning to the old manual pixel-height/timer corrections.
  if (mobileMenu && mobileMenuInner && 'ResizeObserver' in window) {
    const mobileMenuResizeObserver = new ResizeObserver(() => {
      if (!header?.classList.contains('is-mobile-open')) return;
      mobileMenu.style.setProperty(
        '--ridge-mobile-menu-open-height',
        `${Math.ceil(mobileMenuInner.scrollHeight)}px`
      );
    });
    mobileMenuResizeObserver.observe(mobileMenuInner);
  }

  header?.querySelectorAll('[data-mobile-submenu-toggle]').forEach(btn => {
    const children = btn.parentElement?.querySelector('.ridge-mobile-menu__children');
    if (!children) return;

    btn.addEventListener('click', () => {
      const willOpen = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(willOpen));
      children.dataset.open = willOpen ? 'true' : 'false';

      // While the grid row itself is animating, let the cream background track
      // its changing real height directly instead of easing toward stale values.
      mobileMenu?.classList.add('is-submenu-tracking');
      window.clearTimeout(mobileMenu?._ridgeSubmenuTrackingTimer);
      if (mobileMenu) {
        mobileMenu._ridgeSubmenuTrackingTimer = window.setTimeout(() => {
          mobileMenu.classList.remove('is-submenu-tracking');
          if (mobileMenuInner) {
            mobileMenu.style.setProperty(
              '--ridge-mobile-menu-open-height',
              `${Math.ceil(mobileMenuInner.scrollHeight)}px`
            );
          }
        }, 380);
      }
    });
  });

  if (header) {
    // Oura-inspired directional behavior: down hides; upward return reveals glass;
    // approaching the true top continuously dissolves the glass into the page background.
    let lastY = Math.max(0, window.scrollY);
    let ticking = false;
    let returning = false;
    let forceHiddenUntil = 0;
    const HIDE_AFTER = 34;
    const DIRECTION_EPSILON = 2;
    const GLASS_RAMP = 96;

    const setGlass = value => {
      header.style.setProperty('--ridge-glass', String(Math.max(0, Math.min(1, value))));
    };

    const updateHeaderMotion = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;

      if (Date.now() < forceHiddenUntil && !header.classList.contains('is-mega-open')) {
        returning = false;
        header.classList.remove('is-at-top','is-returning','is-scrolled');
        header.classList.add('is-nav-hidden');
        setGlass(0);
        lastY = y;
        return;
      }

      if (y <= 1) {
        returning = false;
        header.classList.remove('is-nav-hidden','is-returning','is-scrolled');
        header.classList.add('is-at-top');
        setGlass(0);
      } else if (delta > DIRECTION_EPSILON && y > HIDE_AFTER && !header.classList.contains('is-mega-open')) {
        returning = false;
        header.classList.remove('is-at-top','is-returning','is-scrolled');
        header.classList.add('is-nav-hidden');
        setGlass(0);
        closeMega(true);
      } else if (delta < -DIRECTION_EPSILON || returning) {
        returning = true;
        header.classList.remove('is-at-top','is-nav-hidden');
        header.classList.add('is-returning','is-scrolled');
        setGlass(Math.min(1, y / GLASS_RAMP));
      }

      lastY = y;
    };

    const requestHeaderMotion = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(updateHeaderMotion); }
    };

    setGlass(window.scrollY > HIDE_AFTER ? 1 : window.scrollY / GLASS_RAMP);
    updateHeaderMotion();
    window.addEventListener('scroll', requestHeaderMotion, { passive: true });
  }

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      closeMega();
      header?.classList.remove('is-mobile-open');
      mobileToggle?.setAttribute('aria-expanded', 'false');
      mobileMenu?.setAttribute('aria-hidden', 'true');
      closeCart();
    }
  });

  // Ridge 3.0.92 — true adaptive floating navigation contrast.
  // The floating/frosted nav ALWAYS follows the actual section beneath it,
  // regardless of the editor's static contrast setting. The editor setting is
  // still respected at the true top of the page.
  if (header) {
    const configuredMode = header.dataset.contrastMode || 'section';
    const themedSections = [...document.querySelectorAll('[data-nav-theme]')];
    let themeTicking = false;

    const setHeaderTheme = theme => {
      header.classList.toggle('is-light', theme === 'light');
      header.classList.toggle('is-dark', theme !== 'light');
    };

    const themeAtTrueTop = () => {
      if (configuredMode === 'light' || configuredMode === 'dark') return configuredMode;
      if (configuredMode === 'blend') return 'light';

      const hero = themedSections.find(section => {
        const r = section.getBoundingClientRect();
        return r.top <= 90 && r.bottom >= 90;
      });
      return hero?.dataset?.navTheme ||
        (document.body.classList.contains('template-index') ? 'light' : 'dark');
    };

    const resolveThemeUnderFloatingNav = () => {
      const nav = header.querySelector('.ridge-nav');
      const frame = header.querySelector('.ridge-header__frame');
      const rect = (nav || frame || header).getBoundingClientRect();

      const sampleY = Math.max(
        1,
        Math.min(window.innerHeight - 1, rect.top + rect.height * 0.56)
      );

      // Use five points across the frosted island so rounded/inset sections
      // don't cause a false read from the page background at one edge.
      const fractions = [0.12, 0.30, 0.50, 0.70, 0.88];
      const votes = [];

      for (const frac of fractions) {
        const x = Math.max(
          1,
          Math.min(window.innerWidth - 1, rect.left + rect.width * frac)
        );

        let winner = null;
        let winnerDepth = -Infinity;

        for (const section of themedSections) {
          const r = section.getBoundingClientRect();
          if (r.top <= sampleY && r.bottom >= sampleY && r.left <= x && r.right >= x) {
            // Prefer the smallest/most-local section when sections overlap.
            const area = Math.max(1, r.width * r.height);
            const depthScore = -area;
            if (depthScore > winnerDepth) {
              winnerDepth = depthScore;
              winner = section;
            }
          }
        }

        if (winner?.dataset?.navTheme) votes.push(winner.dataset.navTheme);
      }

      if (votes.length) {
        const lightVotes = votes.filter(v => v === 'light').length;
        const darkVotes = votes.filter(v => v === 'dark').length;
        return lightVotes > darkVotes ? 'light' : 'dark';
      }

      // Fallback to the section crossing the center of the frosted nav.
      const centerX = Math.max(1, Math.min(window.innerWidth - 1, rect.left + rect.width / 2));
      let fallback = null;
      let fallbackArea = Infinity;

      for (const section of themedSections) {
        const r = section.getBoundingClientRect();
        if (r.top <= sampleY && r.bottom >= sampleY && r.left <= centerX && r.right >= centerX) {
          const area = Math.max(1, r.width * r.height);
          if (area < fallbackArea) {
            fallbackArea = area;
            fallback = section;
          }
        }
      }

      return fallback?.dataset?.navTheme || themeAtTrueTop();
    };

    const updateTheme = () => {
      themeTicking = false;

      // Cream expanded mega sheet always gets dark controls.
      if (header.classList.contains('is-mega-open') || header.classList.contains('is-mega-closing')) {
        setHeaderTheme('dark');
        return;
      }

      // At the true top, respect the configured header mode.
      if (header.classList.contains('is-at-top') || window.scrollY <= 1) {
        setHeaderTheme(themeAtTrueTop());
        return;
      }

      // Once it becomes the floating/frosted nav, always adapt to the section below.
      if (header.classList.contains('is-returning') || header.classList.contains('is-scrolled')) {
        setHeaderTheme(resolveThemeUnderFloatingNav());
        return;
      }

      // Hidden/down-scroll state: precompute the correct color for when it reappears.
      setHeaderTheme(resolveThemeUnderFloatingNav());
    };

    const requestThemeUpdate = () => {
      if (!themeTicking) {
        themeTicking = true;
        requestAnimationFrame(updateTheme);
      }
    };

    updateTheme();
    window.addEventListener('scroll', requestThemeUpdate, { passive: true });
    window.addEventListener('resize', requestThemeUpdate);

    // Re-evaluate when header motion classes change, so the contrast switch happens
    // immediately when the nav becomes floating instead of waiting for another scroll.
    const contrastObserver = new MutationObserver(requestThemeUpdate);
    contrastObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  // Homepage navigation intentionally uses native Shopify URLs and anchors.
  // Shopify's theme preview/editor preserves the active draft theme itself;
  // custom query-string rewriting here caused 404/preview routing failures.
  const hideHeaderForAnchor = () => {
    if (!header) return;
    header.classList.remove('is-mega-open','is-mega-closing','is-returning','is-scrolled');
    header.classList.add('is-nav-hidden');
    header.style.setProperty('--ridge-glass','0');
    closeMega(true);
  };

  // Navbar-directed travel should feel like a steady camera move, not an ease that
  // crawls at the beginning and races through the destination state at the end.
  // CSS `scroll-behavior:smooth` is temporarily disabled so it cannot add a second
  // smoothing layer on top of our requestAnimationFrame movement.
  let ridgeNavScrollRaf = 0;
  const ridgeSlowScrollTo = (targetY) => {
    if (ridgeNavScrollRaf) cancelAnimationFrame(ridgeNavScrollRaf);
    const startY = window.scrollY;
    const delta = targetY - startY;
    const distance = Math.abs(delta);
    if (distance < 2) { window.scrollTo(0, targetY); return; }

    // Distance-based duration keeps the perceived velocity consistent on both
    // desktop and mobile while preventing tiny jumps from feeling sluggish.
    const pxPerMs = window.matchMedia('(max-width:900px)').matches ? 1.10 : 1.25;
    const duration = Math.max(850, Math.min(3000, distance / pxPerMs));
    const start = performance.now();
    const root = document.documentElement;
    const priorBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      // Linear progress is intentional: the UBD reel is itself scroll-driven, so
      // constant page velocity also gives constant reel velocity.
      window.scrollTo(0, startY + delta * t);
      if (t < 1) {
        ridgeNavScrollRaf = requestAnimationFrame(tick);
      } else {
        ridgeNavScrollRaf = 0;
        root.style.scrollBehavior = priorBehavior;
      }
    };
    ridgeNavScrollRaf = requestAnimationFrame(tick);
  };

  const scrollToHashTarget = (id, smooth = true) => {
    const story = document.querySelector('[data-ubd4-story]');
    const featureMap = { 'pure-feel':0, 'heat-flux':1, 'heat-shift':2 };
    if (story && Object.prototype.hasOwnProperty.call(featureMap,id)) {
      // UBD deep links deliberately keep/reveal the navbar and land below it.
      if (header) {
        header.classList.remove('is-nav-hidden','is-mega-open','is-mega-closing');
        header.classList.add('is-scrolled');
        header.style.setProperty('--ridge-glass','1');
        closeMega(true);
      }
      const top = story.getBoundingClientRect().top + window.scrollY;
      const safeTop = window.matchMedia('(max-width:900px)').matches ? 72 : 96;
      const sceneH = Math.max(1, (window.visualViewport?.height || window.innerHeight || 1) - safeTop);
      const travel = Math.max(1, story.offsetHeight - sceneH);
      // Full-open centers of the three reel cycles. Keeping these away from phase
      // boundaries prevents the Heat Shift deep-link from appearing to stop abruptly.
      const centers = [.174,.484,.794];
      const y = top - safeTop + travel * centers[featureMap[id]];
      if (smooth) ridgeSlowScrollTo(Math.max(0,y)); else window.scrollTo(0,Math.max(0,y));
      return true;
    }
    const target = document.getElementById(id);
    if (!target) return false;
    hideHeaderForAnchor();
    const y = target.getBoundingClientRect().top + window.scrollY - 18;
    if (smooth) ridgeSlowScrollTo(Math.max(0,y)); else window.scrollTo(0,Math.max(0,y));
    return true;
  };

  mobileMenu?.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      setMobileOpen(false);
      header?.classList.remove('is-mobile-open','is-mobile-closing');
      mobileToggle?.setAttribute('aria-expanded','false');
      mobileMenu?.setAttribute('aria-hidden','true');
    }, {capture:true});
  });

  document.querySelectorAll('a[href*="#pure-feel"],a[href*="#heat-flux"],a[href*="#heat-shift"],a[href*="#all-for-the-feel"],a[href*="#ugly-by-design"]').forEach(link => {
    link.addEventListener('click', e => {
      let url; try { url = new URL(link.href, window.location.href); } catch (_) { return; }
      const id = url.hash.replace(/^#/,'');
      if (!id || url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      if (window.matchMedia('(max-width:900px)').matches && mobileMenu) {
        setMobileOpen(false);
        header?.classList.remove('is-mobile-open','is-mobile-closing','is-mega-open','is-mega-closing');
        mobileToggle?.setAttribute('aria-expanded','false');
        mobileMenu.setAttribute('aria-hidden','true');
        mobileMenu.style.opacity='0';
        mobileMenu.style.pointerEvents='none';
        document.documentElement.classList.remove('ridge-mobile-menu-open');
        document.body.classList.remove('ridge-mobile-menu-open');
      }
      if (scrollToHashTarget(id, true)) {
        e.preventDefault();
        history.replaceState(null,'',url.hash);
      }
    });
  });

  if (window.location.hash) {
    const id = window.location.hash.replace(/^#/, '');
    setTimeout(() => scrollToHashTarget(id, false), 100);
  }

  // Reusable image galleries.
  document.querySelectorAll('[data-ridge-gallery]').forEach(gallery => {
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const dots = [...gallery.querySelectorAll('[data-gallery-dot]')];
    if (!slides.length) return;
    let index = 0;
    const show = next => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(index - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(index + 1));
    dots.forEach(dot => dot.addEventListener('click', () => show(Number(dot.dataset.galleryDot) || 0)));
  });

  document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
    const slides = [...gallery.querySelectorAll('[data-product-slide]')];
    const dots = [...gallery.querySelectorAll('[data-product-dot]')];
    if (!slides.length) return;
    let index = 0;
    const show = next => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    gallery.querySelector('[data-product-prev]')?.addEventListener('click', () => show(index - 1));
    gallery.querySelector('[data-product-next]')?.addEventListener('click', () => show(index + 1));
    dots.forEach(dot => dot.addEventListener('click', () => show(Number(dot.dataset.productDot) || 0)));
  });

  // Product option buttons -> real Shopify variant id.
  document.querySelectorAll('[data-product-form]').forEach(form => {
    const variantSelect = form.querySelector('[data-variant-select]');
    const variantsNode = form.querySelector('[data-product-variants]');
    let variants = [];
    try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch (_) {}
    const groups = [...form.querySelectorAll('[data-option-index]')];
    const addButton = form.querySelector('[data-add-button]');
    const priceNodes = document.querySelectorAll('[data-product-price]');

    const selectedOptions = () => groups.map(group => group.querySelector('.ridge-option-button.is-selected')?.dataset.optionValue || '');
    const money = cents => {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format((cents || 0) / 100); }
      catch (_) { return `$${((cents || 0) / 100).toFixed(2)}`; }
    };
    const syncVariant = () => {
      const values = selectedOptions();
      const variant = variants.find(v => values.every((value, i) => String(v.options?.[i]) === String(value)));
      if (!variant) {
        if (addButton) { addButton.disabled = true; addButton.textContent = 'Unavailable'; }
        return;
      }
      if (variantSelect) variantSelect.value = String(variant.id);
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'Add to Cart' : 'Sold out';
      }
      priceNodes.forEach(node => node.textContent = money(variant.price));
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    };

    groups.forEach(group => {
      group.querySelectorAll('[data-option-value]').forEach(button => {
        button.addEventListener('click', () => {
          group.querySelectorAll('[data-option-value]').forEach(b => b.classList.remove('is-selected'));
          button.classList.add('is-selected');
          syncVariant();
        });
      });
    });
    syncVariant();

    const qty = form.querySelector('[data-qty-input]');
    form.querySelector('[data-qty-minus]')?.addEventListener('click', () => { if (qty) qty.value = Math.max(1, (Number(qty.value) || 1) - 1); });
    form.querySelector('[data-qty-plus]')?.addEventListener('click', () => { if (qty) qty.value = Math.max(1, (Number(qty.value) || 1) + 1); });

    const sizeToggle = form.querySelector('[data-size-guide-toggle]');
    const sizeGuide = form.querySelector('[data-size-guide]');
    sizeToggle?.addEventListener('click', () => {
      const open = sizeToggle.getAttribute('aria-expanded') === 'true';
      sizeToggle.setAttribute('aria-expanded', String(!open));
      if (sizeGuide) sizeGuide.hidden = open;
    });

    const planInput = form.querySelector('[data-selling-plan-input]');
    const oneTime = form.querySelector('[data-purchase-onetime]');
    const subscribe = form.querySelector('[data-purchase-subscribe]');
    oneTime?.addEventListener('click', () => {
      if (planInput) planInput.value = '';
      oneTime.classList.add('is-selected'); subscribe?.classList.remove('is-selected');
    });
    subscribe?.addEventListener('click', () => {
      if (planInput) planInput.value = subscribe.dataset.sellingPlan || '';
      subscribe.classList.add('is-selected'); oneTime?.classList.remove('is-selected');
    });
  });

  const primaryAdd = document.querySelector('[data-primary-add-button]');
  const stickyBar = document.querySelector('[data-sticky-product-bar]');
  document.querySelectorAll('[data-sticky-add]').forEach(button => {
    button.addEventListener('click', () => primaryAdd?.click());
  });
  if (primaryAdd && stickyBar && 'IntersectionObserver' in window) {
    let primaryIsAbove = false;
    let footerBlocksSticky = false;
    const syncStickyProductBar = () => {
      const shouldShow = primaryIsAbove && !footerBlocksSticky;
      stickyBar.classList.toggle('is-visible', shouldShow);
      stickyBar.classList.toggle('is-footer-blocked', footerBlocksSticky);
      stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    };

    const stickyObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      primaryIsAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      syncStickyProductBar();
    }, { threshold: 0.08, rootMargin: '-88px 0px -12px 0px' });

    const footer = document.querySelector('.ridge-footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(entries => {
        footerBlocksSticky = entries[0].isIntersecting;
        syncStickyProductBar();
      }, { threshold: 0, rootMargin: '0px 0px 120px 0px' });
      footerObserver.observe(footer);
    }

    stickyBar.setAttribute('aria-hidden', 'true');
    stickyObserver.observe(primaryAdd);
  }

  // Cart drawer. Keep the navigation completely stable when the bag opens.
  const drawer = document.querySelector('[data-cart-drawer]');
  const closeCart = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('ridge-cart-drawer-open');
    document.body.style.overflow = '';
  };
  document.querySelectorAll('[data-cart-open]').forEach(opener => opener.addEventListener('click', e => {
    e.preventDefault();
    e.stopPropagation();
    if (!drawer) { window.location.href = opener.href; return; }
    // Preserve the exact current nav appearance. A direct bag click must not alter header geometry,
    // scroll state, glass state, or trigger the mega-menu transition.
    if (header?.classList.contains('is-mega-open') || header?.classList.contains('is-mega-closing')) closeMega(true);
    drawer.classList.add('is-open');
    document.body.classList.add('ridge-cart-drawer-open');
    document.body.style.overflow = 'hidden';
  }));
  drawer?.querySelectorAll('[data-cart-close]').forEach(close => close.addEventListener('click', closeCart));


  // Review cart quantity stepper: update Shopify cart via Ajax and refresh prices in place.
  const formatCartMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency || 'USD' }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return '$' + ((Number(cents) || 0) / 100).toFixed(2);
    }
  };
  document.querySelectorAll('[data-cart-stepper]').forEach(stepper => {
    const input = stepper.querySelector('input');
    const plus = stepper.querySelector('[data-cart-plus]');
    const minus = stepper.querySelector('[data-cart-minus]');
    const line = stepper.closest('[data-cart-line]');
    const form = stepper.closest('[data-review-cart-form]');
    const key = line?.dataset.cartKey;
    const changeUrl = form?.dataset.cartChangeUrl || '/cart/change.js';
    const currency = form?.dataset.currency || 'USD';
    let pending = false;

    const commit = async next => {
      if (!input || !key || pending) return;
      next = Math.max(0, Number(next) || 0);
      pending = true;
      stepper.classList.add('is-loading');
      try {
        const res = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: next })
        });
        if (!res.ok) throw new Error('Cart update failed');
        const cart = await res.json();
        const updatedItem = cart.items.find(item => item.key === key);
        if (next === 0 || !updatedItem) {
          line?.remove();
        } else {
          input.value = updatedItem.quantity;
          const linePrice = line?.querySelector('[data-cart-line-price]');
          if (linePrice) linePrice.textContent = formatCartMoney(updatedItem.final_line_price, currency);
        }
        document.querySelectorAll('[data-cart-subtotal]').forEach(el => { el.textContent = formatCartMoney(cart.total_price, currency); });
        document.querySelectorAll('.ridge-cart-count').forEach(el => { el.textContent = cart.item_count; });
        if (cart.item_count === 0) window.location.reload();
      } catch (err) {
        console.error(err);
        window.location.reload();
      } finally {
        pending = false;
        stepper.classList.remove('is-loading');
      }
    };
    plus?.addEventListener('click', () => commit((parseInt(input?.value || '0', 10) || 0) + 1));
    minus?.addEventListener('click', () => commit((parseInt(input?.value || '0', 10) || 0) - 1));
  });



  // SHOP mega-menu adaptive product-title contrast.
  // Bright/mostly-white product imagery uses Ridge dark text; darker imagery uses white.
  document.querySelectorAll('.ridge-mega-card').forEach(card => {
    const img = card.querySelector('[data-mega-card-image]');
    if (!img) return;
    const classify = () => {
      try {
        const canvas = document.createElement('canvas');
        const size = 32;
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently:true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0,0,size,size).data;
        let total=0, count=0, veryBright=0;
        for (let i=0;i<data.length;i+=4){
          if (data[i+3] < 32) continue;
          const lum = 0.2126*data[i] + 0.7152*data[i+1] + 0.0722*data[i+2];
          total += lum; count++;
          if (lum > 220) veryBright++;
        }
        if (!count) return;
        const avg = total / count;
        const brightShare = veryBright / count;
        card.classList.toggle('is-light-image', avg > 185 || brightShare > .46);
      } catch (_) {
        // If browser/CDN blocks sampling, retain the default high-contrast white title.
      }
    };
    if (img.complete && img.naturalWidth) classify();
    else img.addEventListener('load', classify, { once:true });
  });


});
